import type { DisplayContact } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { Pressable, View } from 'react-native'
import { ActionButton } from '~/components/Actions'
import { SectionContent } from '~/components/layout/Sections'
import { Label } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import {
  BottomSheetSelect,
  type BottomSheetSelectRefHandle,
} from '~/components/ui/select-bottom-sheet'
import { cn } from '~/lib/utils'
import Icon from '../Icon'
import { ContactFlow } from './ContactFlow'
import { ContactListItem } from './ContactListItem'
import { ContactRowCompact } from './ContactRow'

const ADD_CONTACT_ID = '__add_new_contact__'

type AddContactItem = {
  id: typeof ADD_CONTACT_ID
  isAddNew: true
}

type SelectItem = DisplayContact | AddContactItem

function isAddContactItem(item: SelectItem): item is AddContactItem {
  return 'isAddNew' in item && item.isAddNew === true
}

type ContactSelectProps = {
  contacts?: DisplayContact[]
  isLoading?: boolean
  selectedContactIds: string[]
  onChangeSelectedContactIds: (ids: string[]) => void
  label?: string
  sheetTitle?: string
  className?: string
  triggerClassName?: string
  multiple?: boolean
  disabledContactIds?: string[]
  showCreateButton?: boolean
  size?: 'sm' | 'default'
  modalKey?: string
}

export const ContactSelect = memo(function ContactSelect({
  contacts,
  isLoading,
  selectedContactIds,
  onChangeSelectedContactIds,
  multiple = false,
  label = 'Valitse kontakti',
  sheetTitle = 'Valitse kontakti',
  className,
  triggerClassName,
  disabledContactIds,
  showCreateButton = true,
  size = 'default',
  modalKey = 'contact-select',
}: ContactSelectProps) {
  const { t } = useTranslations('contacts')
  const [isAddContactOpen, setAddContactOpen] = useState(false)
  const sanitizedSelectedContactIds = selectedContactIds.filter(Boolean)

  const onSelectToggle = useCallback(
    (contactId: string) => {
      if (!multiple) {
        onChangeSelectedContactIds([contactId])
        return
      }

      const exists = sanitizedSelectedContactIds.includes(contactId)

      const next = exists
        ? sanitizedSelectedContactIds.filter((id) => id !== contactId)
        : [...sanitizedSelectedContactIds, contactId]

      onChangeSelectedContactIds(next)
    },
    [multiple, onChangeSelectedContactIds, sanitizedSelectedContactIds]
  )

  const selectData = useMemo<SelectItem[]>(() => {
    const contactItems = contacts || []

    if (!showCreateButton) {
      const addNewItem: AddContactItem = {
        id: ADD_CONTACT_ID,
        isAddNew: true,
      }

      return [addNewItem, ...contactItems]
    }

    return contactItems
  }, [contacts, showCreateButton])

  const hasContacts = contacts && contacts.length > 0
  const showSelect = hasContacts || !showCreateButton

  const bottomSheetRef = useRef<BottomSheetSelectRefHandle>(null)

  const keyExtractor = useCallback((item: SelectItem) => item.id, [])
  const getItemType = useCallback((item: SelectItem) => {
    if (isAddContactItem(item)) {
      return 'add-contact'
    }

    // eslint-disable-next-line i18next/no-literal-string
    return 'contact'
  }, [])

  return (
    <>
      <View className={cn('gap-4', className)}>
        {showSelect && (
          <BottomSheetSelect<SelectItem>
            ref={bottomSheetRef}
            modalKey={modalKey}
            data={selectData}
            keyExtractor={keyExtractor}
            getItemType={getItemType}
            multiple={multiple}
            values={multiple ? sanitizedSelectedContactIds : undefined}
            value={
              !multiple && sanitizedSelectedContactIds.length > 0
                ? sanitizedSelectedContactIds[0]
                : undefined
            }
            onChangeValues={multiple ? (ids) => onChangeSelectedContactIds(ids) : undefined}
            onChange={
              !multiple
                ? (item) => {
                    if (!isAddContactItem(item)) {
                      onChangeSelectedContactIds([item.id])
                    }
                  }
                : undefined
            }
            label={!hasContacts && showCreateButton ? t(($) => $.select.noContacts) : label}
            disabled={!hasContacts && showCreateButton}
            sheetTitle={sheetTitle}
            footer={
              !multiple
                ? undefined
                : ({ close }) => (
                    <Button variant="primary" size="lg" onPress={close}>
                      {t(($) => $.select.close)}
                    </Button>
                  )
            }
            isLoading={isLoading}
            estimatedItemSize={size === 'sm' ? 56 : 145}
            waitForInitialLayout
            extraData={disabledContactIds}
            className={cn(
              'hover:bg-muted active:bg-muted/50',
              size === 'sm' && 'native:h-10 py-2',
              triggerClassName
            )}
            renderItem={({ item, onSelect, selected, context }) => {
              if (isAddContactItem(item)) {
                return context === 'option' ? (
                  <AddContactOption onPress={() => setAddContactOpen(true)} />
                ) : (
                  <SectionContent className="px-2">{label}</SectionContent>
                )
              }

              const isDisabled = disabledContactIds?.includes(item.id)

              return context === 'option' ? (
                <ContactListItem
                  contact={item}
                  onSelect={!isDisabled ? () => onSelect() : undefined}
                  showAssetName={false}
                  showFeedbackStatus={false}
                  linkToContact={false}
                  buttonVariant="outline"
                  buttonLabel={
                    isDisabled
                      ? t(($) => $.flow.notAvailable)
                      : selected
                        ? t(($) => $.flow.selected)
                        : t(($) => $.flow.open)
                  }
                  isSelected={selected}
                  isDisabled={isDisabled}
                  className={cn('mb-4', selected && !isDisabled && 'bg-muted')}
                />
              ) : sanitizedSelectedContactIds.length > 0 ? (
                <View className="pointer-events-none flex-1">
                  {contacts
                    ?.filter((contact) => sanitizedSelectedContactIds.includes(contact.id))
                    .slice(0, 1)
                    .map((contact) =>
                      size === 'sm' ? (
                        <ContactRowCompact key={contact.id} contact={contact} />
                      ) : (
                        <ContactListItem
                          key={contact.id}
                          contact={contact}
                          showAssetName={false}
                          showFeedbackStatus={false}
                          linkToContact={false}
                          isSelected
                          className="shadow-none! pointer-events-none m-0 max-w-none border-none bg-transparent"
                        />
                      )
                    )}
                  {sanitizedSelectedContactIds.length > 1 && (
                    <View className="mb-1.5 ml-1.5 mt-2 flex-row items-center justify-center gap-2 rounded-md border border-tertiary-foreground/60 bg-tertiary-foreground/50 px-3 py-2">
                      <Label>
                        {t(($) => $.select.and)} {sanitizedSelectedContactIds.length - 1}{' '}
                        {sanitizedSelectedContactIds.length - 1 === 1
                          ? t(($) => $.flow.anotherOne)
                          : t(($) => $.flow.anotherOther)}
                      </Label>
                    </View>
                  )}
                </View>
              ) : (
                <SectionContent className="px-2">{label}</SectionContent>
              )
            }}
          />
        )}

        {showCreateButton && hasContacts && (
          <Label className="text-center font-medium text-lg uppercase">
            {t(($) => $.select.or)}
          </Label>
        )}

        {showCreateButton && (
          <ActionButton
            className="rounded-xl border-2 border-muted py-8 hover:bg-muted"
            action={{
              key: 'create-contact',
              label: t(($) => $.flow.createNew),
              description: hasContacts
                ? t(($) => $.select.createNewDescription)
                : t(($) => $.select.noContactsDescription),
              icon: 'user',
              actionType: 'add',
              onPress: () => setAddContactOpen(true),
              theme: '#B8DBDB',
              buttonVariant: 'primary',
            }}
          />
        )}
      </View>
      <BottomSheet
        modalKey="contact-select-create"
        showCloseButton={false}
        showHandle={false}
        isOpen={isAddContactOpen}
        stackBehavior="push"
        onOpenChange={setAddContactOpen}>
        <ContactFlow
          className="max-h-[90vh] flex-1 sm:max-h-[75vh]"
          onContactSaved={(newContactId) => {
            onSelectToggle(newContactId)
            setAddContactOpen(false)
            bottomSheetRef.current?.close()
          }}
          onCancel={() => {
            setAddContactOpen(false)
          }}
          isInBottomSheet
        />
      </BottomSheet>
    </>
  )
})

const AddContactOption = memo(function AddContactOption({ onPress }: { onPress: () => void }) {
  const { t } = useTranslations('contacts')
  return (
    <Pressable
      onPress={onPress}
      className="group/button native:shadow-card mx-2 mb-4 flex-row items-center gap-4 rounded-xl bg-card px-4 py-4 shadow-sm shadow-primary/20 transition-colors duration-150 hover:bg-accent active:bg-accent">
      <View className="items-center justify-center rounded-lg bg-muted p-3 transition-colors duration-150 group-hover/button:bg-accent">
        <Icon
          name="add"
          size={24}
          className="text-foreground transition-colors duration-150 group-hover/button:text-accent-foreground"
        />
      </View>
      <Heading3 className="transition-colors duration-150 group-hover/button:text-accent-foreground">
        {t(($) => $.flow.createNew)}
      </Heading3>
    </Pressable>
  )
})
